/**
 * BankReconciliationTab Component
 * 
 * This component provides the interface for bank reconciliation functionality.
 * It displays features overview and launches the reconciliation modal.
 * 
 * Features:
 * - Auto-matching of transactions by amount and date
 * - Bank statement CSV import
 * - Manual transaction matching
 * - Complete audit trail and reporting
 * - Full internationalization support (Arabic/English)
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScaleIcon } from '@heroicons/react/24/outline';
import BankReconciliationModal from '../BankReconciliationModal';

const BankReconciliationTab = () => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);

  const handleSuccess = () => {
    setShowModal(false);
    // Optionally refresh data
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {t('bank_reconciliation.title', 'Bank Reconciliation')}
          </h2>
          <p className="text-gray-600 mt-1">
            {t('accounting.description')}
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
        >
          <ScaleIcon className="h-4 w-4" />
          <span>{t('bank_reconciliation.start_reconciliation', 'Start Reconciliation')}</span>
        </button>
      </div>

      {/* Feature Description */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">
          {t('payment_analytics.features')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start space-x-3">
            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
            <div>
              <h4 className="font-medium text-blue-900">{t('invoices.auto_match')}</h4>
              <p className="text-blue-700 text-sm">{t('bank_reconciliation.auto_match_desc', 'Automatically match transactions by amount and date')}</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
            <div>
              <h4 className="font-medium text-blue-900">{t('invoices.upload_statement')}</h4>
              <p className="text-blue-700 text-sm">{t('bank_reconciliation.upload_statement_desc', 'Import bank statements in CSV format')}</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
            <div>
              <h4 className="font-medium text-blue-900">{t('bank_reconciliation.manual_match', 'Manual Matching')}</h4>
              <p className="text-blue-700 text-sm">{t('bank_reconciliation.manual_match_desc', 'Manually match transactions for complex cases')}</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
            <div>
              <h4 className="font-medium text-blue-900">{t('accounting.audit_trail')}</h4>
              <p className="text-blue-700 text-sm">{t('bank_reconciliation.audit_trail_desc', 'Complete reconciliation history and reporting')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <BankReconciliationModal
          onClose={() => setShowModal(false)}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
};

export default BankReconciliationTab;
